import { z } from "zod";
import type { ProviderKind } from "./model-providers.ts";

/**
 * Model parameters (openspec change `model-picking`, D15, Q17): effort stays the only user knob.
 * The catalogue declares what each model supports; ESI maps the effort to a reasoning level,
 * then to the provider's native setting. This module is data and pure functions: the adapter of
 * each provider kind applies the native setting (ADR 0002), core never sees provider options.
 */

/** ESI's reasoning levels, least to most. */
export const REASONING_LEVELS = ["off", "low", "medium", "high", "max"] as const;
export const reasoningLevelSchema = z.enum(REASONING_LEVELS);
export type ReasoningLevel = z.infer<typeof reasoningLevelSchema>;
/** The levels a reasoning model may run at (every level but `off`). */
export type ActiveReasoningLevel = Exclude<ReasoningLevel, "off">;

/**
 * How a model reasons natively (D15, amended by Q18b: keyed by style, since every lab reaches
 * Bedrock through one kind): `adaptive` takes an effort level (Claude 5.x adaptive thinking),
 * `budget` a thinking budget in tokens (Claude Haiku 4.5), `reasoning-config` Amazon Nova 2's
 * `reasoningConfig` with a maximum effort (`low` or `medium`; `high` forbids an output limit).
 */
export const REASONING_STYLES = ["adaptive", "budget", "reasoning-config"] as const;
export type ReasoningStyle = (typeof REASONING_STYLES)[number];

/** What a model supports for reasoning (curated per offering); null: it never reasons. */
export interface ReasoningSupport {
  readonly style: ReasoningStyle;
  readonly levels: readonly ActiveReasoningLevel[];
}

/** Every level, for the curated entries. */
export const ALL_REASONING_LEVELS: readonly ActiveReasoningLevel[] = [
  "low",
  "medium",
  "high",
  "max",
];

/** The parameters a catalogued model supports (D15). */
export interface ModelParameterSupport {
  readonly reasoning: ReasoningSupport | null;
  /** Input context window in tokens (canonical `limit.context`). */
  readonly contextWindow: number;
  /** Largest output in tokens (canonical `limit.output`). */
  readonly maxOutput: number;
}

/**
 * The output tokens an adaptive reasoning call adds to the answer's budget at `high` (unchanged
 * since D1); the other levels add {@link REASONING_OUTPUT_TOKENS_BY_LEVEL}.
 */
export const REASONING_OUTPUT_TOKENS = 16_000;

/** Thinking budgets of the `budget` style, per level (D15). */
export const REASONING_BUDGET_TOKENS: Readonly<Record<ActiveReasoningLevel, number>> = {
  low: 4_096,
  medium: 8_192,
  high: 16_000,
  max: 32_000,
};

/**
 * The output tokens an adaptive call adds per level (Q17 amended: every effort reasons, so the
 * room for thinking is sized by its level, `high` keeping D1's 16 000).
 */
export const REASONING_OUTPUT_TOKENS_BY_LEVEL: Readonly<Record<ActiveReasoningLevel, number>> = {
  low: 4_096,
  medium: 8_192,
  high: REASONING_OUTPUT_TOKENS,
  max: 32_000,
};

/** The answer budget Bedrock's Anthropic adapter adds a thinking budget to when none is set. */
const DEFAULT_ANSWER_TOKENS = 4_096;

/** The smallest thinking budget a provider accepts (Anthropic: 1 024). */
const MIN_BUDGET_TOKENS = 1_024;

/**
 * The native reasoning setting of one call, by provider family: Anthropic (Bedrock or direct)
 * adaptive thinking with an effort, Anthropic thinking with a budget, or an OpenAI reasoning
 * effort. Absent: nothing is sent (the model's default, no thinking for Anthropic).
 */
export type NativeReasoning =
  | { readonly type: "anthropic-adaptive"; readonly effort: ActiveReasoningLevel }
  | { readonly type: "anthropic-budget"; readonly budgetTokens: number }
  | { readonly type: "openai-effort"; readonly effort: "low" | "medium" | "high" }
  | { readonly type: "reasoning-config"; readonly effort: "low" | "medium" };

/** The parameters a candidate's call carries: the ESI level, its native form and output budget. */
export interface ModelCallParameters {
  /** The level used (`off` when the model or the call does not reason). */
  readonly level: ReasoningLevel;
  readonly native?: NativeReasoning;
  /**
   * The call's output budget once reasoning is added (answer + thinking), capped at the
   * model's maximum output; absent when unchanged.
   */
  readonly maxOutputTokens?: number;
}

/** The parameters of a call that does not reason. */
export const NO_REASONING: ModelCallParameters = { level: "off" };

/**
 * The effort's level on one model (D15): the requested level when the model reasons and
 * supports it, else the closest supported level below it, else `off`.
 */
export function supportedLevel(
  support: ReasoningSupport | null,
  level: ReasoningLevel,
): ReasoningLevel {
  if (!support || level === "off") {
    return "off";
  }
  const wanted = REASONING_LEVELS.indexOf(level);
  const below = support.levels.filter((l) => REASONING_LEVELS.indexOf(l) <= wanted);
  return below.at(-1) ?? "off";
}

/**
 * The native setting and output budget of a call at `level` (D15), per provider kind and, on
 * Bedrock, per reasoning style: Anthropic adaptive thinking (`effort` = the level), or a
 * thinking budget sized by the level and capped so budget plus answer fit the model's maximum
 * output; Nova 2's `reasoningConfig` effort (the levels it declares); OpenAI's reasoning effort
 * (`max` → `high`). `off` sends nothing for every kind ESI routes today.
 *
 * @param answerTokens the call's own `maxOutputTokens` (undefined: the provider's default).
 */
export function callParameters(input: {
  readonly kind: ProviderKind;
  readonly support: ModelParameterSupport;
  readonly level: ReasoningLevel;
  readonly answerTokens: number | undefined;
}): ModelCallParameters {
  const { kind, support, answerTokens } = input;
  const level = supportedLevel(support.reasoning, input.level);
  if (level === "off" || !support.reasoning) {
    return NO_REASONING;
  }
  // A model without a known maximum output is not capped (the provider enforces its own).
  const maxOutput = support.maxOutput > 0 ? support.maxOutput : Number.POSITIVE_INFINITY;
  switch (kind) {
    case "bedrock":
    case "anthropic": {
      if (support.reasoning.style === "reasoning-config") {
        if (kind !== "bedrock" || (level !== "low" && level !== "medium")) {
          return NO_REASONING;
        }
        return {
          level,
          native: { type: "reasoning-config", effort: level },
          maxOutputTokens: Math.min(
            (answerTokens ?? 0) + REASONING_OUTPUT_TOKENS_BY_LEVEL[level],
            maxOutput,
          ),
        };
      }
      if (support.reasoning.style === "adaptive") {
        return {
          level,
          native: { type: "anthropic-adaptive", effort: level },
          maxOutputTokens: Math.min(
            (answerTokens ?? 0) + REASONING_OUTPUT_TOKENS_BY_LEVEL[level],
            maxOutput,
          ),
        };
      }
      // The provider adds the budget to the answer's output itself (to its default answer
      // budget when the call sets none); keep the sum in bounds.
      const room = maxOutput - (answerTokens ?? DEFAULT_ANSWER_TOKENS);
      const budgetTokens = Math.min(REASONING_BUDGET_TOKENS[level], room);
      if (budgetTokens < MIN_BUDGET_TOKENS) {
        return NO_REASONING;
      }
      return { level, native: { type: "anthropic-budget", budgetTokens } };
    }
    case "openai":
    case "azure-openai":
      return {
        level,
        native: { type: "openai-effort", effort: level === "max" ? "high" : level },
      };
    default:
      // No native reasoning setting known for the kind yet: the call goes without.
      return NO_REASONING;
  }
}

/** The provider kind of a candidate's provider family (`amazon-bedrock` → `bedrock`). */
export function providerKindOfFamily(provider: string): ProviderKind | undefined {
  return provider === "amazon-bedrock" ? "bedrock" : undefined;
}
